import { Component, OnInit, ViewChild } from '@angular/core';
import { TransaccionService } from '../../services/transaccion.service';
import { Transaccion } from '../../interfaces/transaccion.interface';
import { MatTableDataSource } from '@angular/material/table';
import { Subscription } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { CurrencyPipe } from '@angular/common';
import { Clipboard } from '@angular/cdk/clipboard';
import Swal from 'sweetalert2';
import { MatPaginator } from '@angular/material/paginator';
import { SpinnerService } from 'src/app/shared/services/spinner.service';
import { RejectTransactionWithdrawModalComponent } from '../withdraw-trx-list/reject-transaction-withdraw-modal/reject-transaction-withdraw-modal.component';
import { PayShinkansenModalComponent } from '../withdraw-trx-list/pay-shinkansen-modal/pay-shinkansen-modal.component';
import { ShinkansenPayoutService } from '../../services/shinkansen-payout.service';
import { firstValueFrom } from 'rxjs';

/**
 * Techo de seguridad al paginar `manual_pending`: si la bandeja creciera sin
 * control, la pantalla no se cuelga pidiendo páginas para siempre.
 */
const MAX_MANUAL_PENDING = 2000;

/** `autoAttemptOrigin` del backend → por qué no salió el pago, en lenguaje claro. */
const MANUAL_PENDING_ORIGINS: { [key: string]: string } = {
  limit_exceeded: 'Supera el tope de pago automático',
  pre_send_validation: 'Banco o datos de la cuenta no soportados',
  shinkansen_error: 'Shinkansen rechazó el envío',
  network_error: 'No hubo respuesta al enviar',
  shinkansen_reversed: 'Shinkansen reversó el pago',
  unexpected_error: 'Error inesperado en el envío automático',
};

@Component({
  selector: 'app-withdraw-trx-sent',
  templateUrl: './withdraw-trx-sent.component.html',
  styleUrls: ['./withdraw-trx-sent.component.sass']
})
export class WithdrawTrxSentComponent implements OnInit {
  transacciones: any[] = [];
  seleccionados: number = 0;
  dataSource: MatTableDataSource<any> | undefined;
  subscription: Subscription = new Subscription();
  displayedColumns = ['id', 'createdAt', 'fechaTermino', 'estado', 'userId', 'cuentaRetiroId', 'valorRetiro', 'bancoDestino', 'cuentaDestino', 'identificacion', 'operador', 'acciones'];
  expandedCardId: number | null = null;
  emailOperador = '';
  public currentPage = 1;
  public pageSize = 25;
  public totalItems = 0;
  public totalPages = this.totalItems / this.pageSize;
  /** reference (String(tx.id)) → payout manual_pending, para badge + motivo. */
  private manualPendingMap = new Map<string, any>();
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator | null = null;
  constructor(
    private transactionService: TransaccionService,
    private currencyPipe: CurrencyPipe,
    private clipboard: Clipboard,
    private spinner: SpinnerService,
    private dialog: MatDialog,
    private shinkansenPayoutService: ShinkansenPayoutService,
  ) { }
  async ngOnInit() {
    await this.loadTransactions();
  }

  async loadTransactions() {
    const ref = this.spinner.start();

    const data = await this.transactionService.getAllWithdrawTransactions('ENVIADA,RECHAZADA', this.currentPage, this.pageSize);

    this.transacciones = data.query;
    this.dataSource = new MatTableDataSource(this.transacciones);
    this.totalItems = +data.count;
    this.totalPages = Math.ceil(this.totalItems / this.pageSize);

    // Marcar cuáles quedaron en pago manual pendiente (envío auto Shinkansen falló).
    // 1 sola llamada al backend en vez de consultar estado fila por fila.
    await this.loadManualPending();

    this.spinner.stop(ref);
  }

  /**
   * Trae los payouts en `manual_pending` y arma un mapa por `reference` (=id de la
   * tx). Las filas ENVIADA que matcheen muestran badge "pago manual pendiente" +
   * botón Pagar. Best-effort: si falla, no rompe la lista.
   */
  private async loadManualPending(): Promise<void> {
    this.manualPendingMap.clear();
    try {
      // Se pagina hasta traerlos todos: con una sola llamada de 200 los casos más
      // viejos quedaban sin badge ni acción, o sea invisibles para siempre.
      const pageSize = 200;
      let offset = 0;
      let total = Infinity;

      while (offset < total && offset < MAX_MANUAL_PENDING) {
        const res = await firstValueFrom(
          this.shinkansenPayoutService.getManualPending(pageSize, offset),
        );
        const items = res?.items || [];
        total = Number(res?.total ?? items.length);

        items.forEach((item: any) => {
          if (item?.reference) {
            this.manualPendingMap.set(String(item.reference), item);
          }
        });

        if (items.length < pageSize) break;
        offset += pageSize;
      }
    } catch {
      // silencioso: la lista de enviadas sigue funcionando sin los badges
    }
  }

  /**
   * true si esta tx quedó con un payout Shinkansen en manual_pending: el envío
   * automático no salió y la plata todavía no se movió.
   *
   * Se exige ENVIADA: una RECHAZADA ya devolvió el saldo al usuario, así que
   * ofrecer "Pagar" sobre ella sería transferir plata de una operación anulada.
   */
  isManualPending(row: any): boolean {
    if (String(row?.estado || '').toUpperCase() !== 'ENVIADA') return false;
    return this.manualPendingMap.has(String(row?.id));
  }

  /** Motivo del fallo (autoAttemptOrigin + errorMessage) para tooltip del badge. */
  getManualPendingReason(row: any): string {
    const item = this.manualPendingMap.get(String(row?.id));
    if (!item) return '';
    const parts: string[] = [];
    if (item.autoAttemptOrigin) parts.push(`Origen: ${item.autoAttemptOrigin}`);
    if (item.errorMessage) parts.push(`Detalle: ${item.errorMessage}`);
    return parts.join(' | ');
  }

  /**
   * Motivo legible para mostrar en la fila, sin depender de que el operador
   * pase el mouse por encima del badge.
   */
  getManualPendingShortReason(row: any): string {
    const item = this.manualPendingMap.get(String(row?.id));
    if (!item) return '';
    return MANUAL_PENDING_ORIGINS[item.autoAttemptOrigin] || 'El envío automático no salió';
  }

  /**
   * Abre el modal de pago Shinkansen para una tx que quedó en manual_pending.
   * El modal acepta la fila tal cual (mismos campos que la pantalla de pendientes).
   */
  openPayShinkansenModal(row: any) {
    const dialogRef = this.dialog.open(PayShinkansenModalComponent, {
      data: row,
      maxHeight: '95vh',
      maxWidth: '900px',
      width: '90%',
      panelClass: 'responsive-dialog',
    });
    dialogRef.afterClosed().subscribe(async (result) => {
      if (result === true) {
        this.loadTransactions();
      }
    });
  }

  /**
   * Registra que el retiro se pagó por fuera de Shinkansen (transferencia hecha
   * a mano desde el banco).
   *
   * Es la pieza que faltaba: hasta ahora un pago manual no dejaba rastro, la fila
   * seguía en `manual_pending` y mañana otro operador veía el botón "Pagar"
   * sobre un retiro ya pagado. Cerrarla también bloquea en el backend cualquier
   * pago posterior de esa transacción.
   */
  async markAsPaidManually(row: any) {
    const monto = this.currencyPipe.transform(row?.totalMonedaDestino, '$') || '';
    const nombre = `${row?.nombreUsuario || ''} ${row?.apellidosUsuario || ''}`.trim();

    const confirm = await Swal.fire({
      title: '¿Esta transferencia ya salió?',
      html:
        `<p style="font-size:.95rem;color:#374151;text-align:left;margin:0;">` +
        `Retiro <b>#${row?.id}</b> · <b>${monto}</b> · ${nombre}<br>` +
        `<span style="color:#6b7280;font-size:.85rem;">` +
        `${row?.bancoNombre || 's/banco'} · ${row?.numeroCuenta || 's/cuenta'}</span>` +
        `</p>` +
        `<p style="font-size:.85rem;color:#6b7280;text-align:left;margin-top:12px;">` +
        `Si confirmas, el retiro deja de figurar como pendiente y no se va a poder pagar de nuevo.` +
        `</p>`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sí, ya salió',
      cancelButtonText: 'No',
      confirmButtonColor: '#1C593D',
    });

    if (!confirm.isConfirmed) return;

    // Sin formulario a propósito: el operador que limpia una fila de hace semanas
    // no tiene a mano el comprobante, y pedírselo convertía el cierre en algo que
    // nadie hacía. El rastro de quién y cuándo lo pone el backend.
    const ref = this.spinner.start();
    try {
      const res = await firstValueFrom(
        this.shinkansenPayoutService.resolveManualPending(String(row?.id), {
          outcome: 'manual_paid',
        }),
      );
      this.manualPendingMap.delete(String(row?.id));
      await Swal.fire({
        icon: 'success',
        title: 'Listo',
        text: res?.message || 'El retiro quedó cerrado.',
        confirmButtonColor: '#1C593D',
      });
      await this.loadTransactions();
    } catch (error: any) {
      await Swal.fire({
        icon: 'error',
        title: 'No se pudo cerrar',
        text: error?.message || 'Intenta de nuevo en unos minutos.',
        confirmButtonColor: '#1C593D',
      });
    } finally {
      this.spinner.stop(ref);
    }
  }

  onPageChange(event: any) {
    this.pageSize = event.pageSize;
    this.currentPage = event.pageIndex + 1;
    this.loadTransactions();
  }

  toggleCard(id: number) {
    this.expandedCardId = this.expandedCardId === id ? null : id;
  }

  getStatusBg(row: any): string {
    const estado = row.estado?.toUpperCase() || '';
    if (estado.includes('ENVIAD')) return '#16a34a';
    if (estado.includes('COMPLETAD')) return '#15803d';
    if (estado.includes('PAGAD')) return '#166534';
    if (estado.includes('RECHAZAD')) return '#dc2626';
    if (estado.includes('CANCELAD')) return '#991b1b';
    if (estado.includes('PROCESO') || estado.includes('PROCESAR')) return '#f59e0b';
    if (estado.includes('PENDIENTE')) return '#eab308';
    if (estado.includes('REVIS')) return '#8b5cf6';
    if (estado.includes('EXPIRAD')) return '#6b7280';
    return '#f3f4f6';
  }

  openRejectTransactionModal(row: any) {
    const isMobile = window.innerWidth < 768;
    const dialogRef = this.dialog.open(RejectTransactionWithdrawModalComponent, {
      data: row,
      maxHeight: isMobile ? '95vh' : '70%',
      maxWidth: isMobile ? '95vw' : '826px',
      minWidth: isMobile ? '95vw' : '800px',
    });
    dialogRef.afterClosed().subscribe(async (result) => {
      if (result === true) {
        this.loadTransactions();
      }
    });
  }

  copyData(data: any) {
    const id = data.id || '';
    const fechaCreacion = data.createdAt || '';
    const nombreUsuario = data.nombreUsuario + ' ' + data.apellidosUsuario || '';
    const aliasCuentaRetiro = data.alias || '';
    const montoRetiro = data.totalMonedaDestino || '';
    const bancoDestino = data.bancoNombre || '';
    const cuentaDestino = data.tipoCuentaNombre + ' ' + data.numeroCuenta || '';
    const identificacion = data.tipoIdentificacion + ' ' + data.numeroId || '';

    this.clipboard.copy(`Id: ${id}\n Fecha de Creacion: ${fechaCreacion}\n Nombre: ${nombreUsuario}\n Alias Cuenta Retiro: ${aliasCuentaRetiro}\n Monto Retiro: ${montoRetiro}\n Banco Destino: ${bancoDestino}\n Cuenta Destino: ${cuentaDestino}\n Identificacion: ${identificacion}`);
    const Toast = Swal.mixin({
      toast: true,
      position: 'top-end',
      showConfirmButton: false,
      timer: 3000,
      timerProgressBar: true,
    })

    Toast.fire({
      icon: 'success',
      title: 'Transferencia copiada al portapapeles'
    })
  }

  change() {
    this.seleccionados =
      this.dataSource?.data.filter((value) => value.seleccionado).length || 0;

  }

  limpiarSeleccion() {
    const data =
      this.dataSource?.data.map((value) => {
        return { ...value, seleccionado: false };
      }) || [];

    if (this.dataSource && this.dataSource.data) {
      this.dataSource.data = data;
    }
    this.change();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    if (this.dataSource) {
      this.dataSource.filter = filterValue.trim().toUpperCase();
    }
  }

  public doFilter = (event: any) => {
    const element = event.currentTarget as HTMLInputElement;
    const value = element.value;
    if (this.dataSource)
      this.dataSource.filter = value.trim().toLocaleLowerCase();
  };
  //TPDP

  returnSelectedTransactions(row: Transaccion) {
    const selected =
      this.dataSource?.data?.filter((x) => x.seleccionado && x.id !== row.id) ||
      [];
    selected.push(row);
    return selected;
  }

  disableButton(email: string) {
    if (this.emailOperador !== email) return true;
    return false;
  }
}
